import { SignIn } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { Shield, BarChart3, Users, Calendar, Leaf } from 'lucide-react';

interface AdminLoginPageProps {
  isDarkMode: boolean;
}

export default function AdminLoginPage({ isDarkMode: _isDarkMode }: AdminLoginPageProps) {
  return (
    <div className="min-h-screen flex w-full bg-stone-950 transition-colors duration-300">
      {/* Left Side - Brand/Visual */}
      <div className="hidden lg:flex w-1/2 bg-stone-900 relative flex-col justify-between p-16 text-white overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] bg-sage/30 rounded-full blur-[120px]" />
          <div className="absolute top-[40%] -right-[10%] w-[60%] h-[60%] bg-emerald-600/20 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-sage rounded-xl flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold">SereneTouch Admin</span>
          </div>

          <h1 className="text-5xl font-bold leading-[1.1] mb-8">
            Command Center for<br/>
            <span className="text-sage">Your Platform.</span>
          </h1>
          <p className="text-lg text-stone-300 max-w-md leading-relaxed">
            Monitor studios, track subscriptions, analyze performance, and manage your SereneTouch platform from one powerful dashboard.
          </p>
        </div>

        {/* Feature highlights */}
        <div className="relative z-10">
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="flex items-center gap-3 bg-white/10 rounded-lg p-4">
              <div className="w-10 h-10 rounded-lg bg-sage/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-sage" />
              </div>
              <div>
                <p className="font-medium">Real-time Analytics</p>
                <p className="text-sm text-stone-400">Track everything</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/10 rounded-lg p-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="font-medium">Studio Management</p>
                <p className="text-sm text-stone-400">Full control</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/10 rounded-lg p-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="font-medium">Booking Insights</p>
                <p className="text-sm text-stone-400">Platform-wide</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/10 rounded-lg p-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="font-medium">Secure Access</p>
                <p className="text-sm text-stone-400">Admin only</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-stone-500">
            SereneTouch Admin Portal · v{__APP_VERSION__}
          </p>
        </div>
      </div>

      {/* Right Side - Clerk Auth */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-4 sm:p-8 bg-stone-950 relative min-h-screen">

        {/* Mobile Header */}
        <div className="lg:hidden absolute top-8 left-8 flex items-center gap-2">
          <div className="w-8 h-8 bg-sage rounded-lg flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-white">SereneTouch Admin</span>
        </div>

        <div className="w-full max-w-md bg-stone-900 rounded-2xl shadow-xl p-8 pt-12 border border-stone-800">
          <div className="mb-8 text-center">
            <div className="w-14 h-14 bg-sage/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Leaf className="w-8 h-8 text-sage" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Welcome to Admin Portal
            </h2>
            <p className="text-stone-400">
              Sign in to access the admin portal
            </p>
          </div>

          <SignIn
            appearance={{
              baseTheme: dark,
              elements: {
                rootBox: 'w-full',
                cardBox: 'w-full',
                card: 'shadow-none bg-transparent p-0 m-0 w-full',
                main: 'w-full gap-4',
                header: 'hidden',
                headerTitle: 'hidden',
                headerSubtitle: 'hidden',
                logoBox: 'hidden',
                logoImage: 'hidden',
                socialButtons: 'w-full',
                socialButtonsBlockButton: 'border-stone-700 hover:bg-stone-800 rounded-xl h-12 font-medium overflow-visible',
                socialButtonsBlockButtonText: 'text-stone-300 font-medium',
                socialButtonsProviderIcon: 'w-5 h-5',
                alternativeMethods: 'w-full',
                alternativeMethodsBlockButton: 'w-full border-stone-700 rounded-xl',
                dividerRow: 'my-4',
                dividerLine: 'bg-stone-700',
                dividerText: 'text-stone-500 text-sm',
                form: 'w-full gap-4',
                formFieldRow: 'mb-4',
                formFieldLabel: 'text-stone-300 font-medium text-sm',
                formFieldInput: 'bg-stone-800 border-stone-700 text-white focus:ring-sage focus:border-sage rounded-xl h-12',
                formFieldInputShowPasswordButton: 'text-stone-400',
                formButtonPrimary: 'bg-sage hover:bg-sage/90 text-white shadow-lg shadow-sage/25 rounded-xl h-12 text-base font-semibold',
                footerAction: 'hidden',
                footer: 'hidden',
                identityPreview: 'bg-stone-800 border-stone-700 rounded-xl',
                identityPreviewText: 'text-white',
                identityPreviewEditButton: 'text-sage',
                footerActionLink: 'text-sage hover:text-sage/80',
              },
              layout: {
                socialButtonsPlacement: 'top',
                socialButtonsVariant: 'blockButton',
              },
            }}
            routing="hash"
          />

          {/* Info note */}
          <div className="mt-6 pt-6 border-t border-stone-800 text-center">
            <p className="text-stone-500 text-sm">
              This portal is invite-only. Contact an admin to request access.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-8 text-center">
          <p className="text-sm text-stone-500">
            Admin Portal at admin.serenetouch.app
          </p>
        </div>
      </div>
    </div>
  );
}
